import React, { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { type ProfileShortInterface, type Profile } from 'data-access/market/type/market-profile-type'
import { dataAccessProfiles } from 'data-access/market/data-access-profiles'
import { Loader } from 'ui/core/loader'
import { useLocation, useParams } from 'react-router-dom'
import { useResolution } from 'util/hooks/useResolution'
import FeatureError from 'feature/error/feature-error'
import { type MarketCategoryInterface } from 'data-access/market/type/market-category-type'
import { type MarketClassificationInterface } from 'data-access/market/type/market-classification-type'
import { dataAccessCategories } from 'data-access/market/data-access-categories'
import { dataAccessClassifications } from 'data-access/market/data-access-classifications'
import ProfileModal from '../profile/modal/profile-modal'

export type CatalogViewType = 'list' | 'grid'

interface MarketContextProps {
  catalogView: CatalogViewType
  setCatalogView: (value: CatalogViewType) => void
  isProfile: boolean
  profile?: Profile | null
  profileId?: string | null
  isCategory: boolean
  category?: MarketCategoryInterface | null
  classification?: MarketClassificationInterface | null
  setProfilesListModal: (profiles: ProfileShortInterface[]) => void
}

const MarketContext = createContext<MarketContextProps>({
  catalogView: 'grid',
  setCatalogView: () => {},
  isProfile: false,
  profile: null,
  profileId: null,
  isCategory: false,
  category: null,
  classification: null,
  setProfilesListModal: () => {}
})

export const MAX_INITIAL_PROFILES = 20

export const MarketProvider: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const [catalogView, setCatalogViewState] = useState<CatalogViewType>(
    (localStorage.getItem('catalogView') as CatalogViewType) ?? 'grid'
  )
  const [isProfile, setIsProfile] = useState<boolean>(false)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [isCategory, setIsCategory] = useState<boolean>(false)
  const [category, setCategory] = useState<MarketCategoryInterface | null>(null)
  const [classification, setClassification] = useState<MarketClassificationInterface | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [profilesListModal, setProfilesListModal] = useState<ProfileShortInterface[]>([])

  const { pathname } = useLocation()
  const { mobile } = useResolution()

  const { classificationId, categoryId, profileId } = useParams()

  const setCatalogView = useCallback((value: CatalogViewType) => {
    setCatalogViewState(value)
    if (!mobile) {
      localStorage.setItem('catalogView', value)
    }
  }, [mobile])

  useEffect(() => {
    if (mobile) {
      setCatalogView('grid')
    } else {
      const storedCatalogView = localStorage.getItem('catalogView') as CatalogViewType
      if (storedCatalogView) {
        setCatalogView(storedCatalogView)
      }
    }
  }, [mobile, setCatalogView])

  // SET PROFILE
  const loadProfile = useCallback(async (id: string) => {
    setIsLoading(true)
    try {
      const data = await dataAccessProfiles.loadProfileById(id)
      setProfile(data)
      setIsProfile(!!data)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [])
  useEffect(() => {
    if (profileId) {
      void loadProfile(profileId)
    } else {
      setIsProfile(false)
      setProfile(null)
    }
  }, [pathname, profileId])

  // SET CATEGORY
  const loadCategory = useCallback(async (id: string): Promise<void> => {
    setIsLoading(true)
    try {
      const category = await dataAccessCategories.getCategory(id)
      setCategory(category)
      setIsCategory(!!category)
    } catch (error: any) {
      setError(error.message)
      throw error // to catch in the parent
    } finally {
      setIsLoading(false)
    }
  }, [])
  useEffect(() => {
    if (categoryId) {
      void loadCategory(categoryId)
    } else {
      setCategory(null)
    }
  }, [pathname, categoryId])

  // SET CLASSIFICATION
  const loadClassification = useCallback(async (id: string) => {
    setIsLoading(true)
    try {
      const classification = await dataAccessClassifications.getClassification(id)
      setClassification(classification)
    } catch (error: any) {
      setError(error.message)
      throw error // to catch in the parent
    } finally {
      setIsLoading(false)
    }
  }, [])
  useEffect(() => {
    if (classificationId) {
      void loadClassification(classificationId)
    } else {
      setClassification(null)
    }
  }, [pathname, classificationId])

  const contextValue = useMemo(() => ({
    catalogView,
    setCatalogView,
    isProfile,
    profile,
    profileId,
    isCategory,
    category,
    classification,
    setProfilesListModal,
    profilesListModal
  }), [
    catalogView,
    setCatalogView,
    isProfile,
    profile,
    profileId,
    isCategory,
    category,
    classification,
    setProfilesListModal,
    profilesListModal
  ])

  if (error) {
    return <FeatureError error={error} />
  }

  return (
    <MarketContext.Provider value={contextValue}>
      {isLoading && <Loader />}
      {children}
      {profilesListModal?.length
        ? <ProfileModal
            open={!!profilesListModal?.length}
            onClose={() => { setProfilesListModal([]) }}
            profiles={profilesListModal}
            categoryId={category?.id}
          />
        : null
      }
    </MarketContext.Provider>
  )
}

export const useMarket = (): MarketContextProps => {
  const context = useContext(MarketContext)
  if (context === undefined) {
    throw new Error('useMarket must be used within a MarketProvider')
  }
  return context
}
